import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { cn } from '@/utils/style';
import { VariantProps, cva } from 'class-variance-authority';

type AccordionContextType = {
  initialMount: true | undefined;
};

const AccordionContext = createContext<AccordionContextType | undefined>(undefined);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (context === undefined) {
    throw new Error('useAccordionContext must be used within an AccordionProvider');
  }
  return context;
};

const AccordionProvider: React.FC<
  React.PropsWithChildren<AccordionPrimitive.AccordionSingleProps | AccordionPrimitive.AccordionMultipleProps>
> = ({ children, ...props }) => {
  const [initialMount, setInitialMount] = useState<true | undefined>(true);

  useEffect(() => {
    setInitialMount(undefined);
  }, []);

  return (
    <AccordionContext.Provider value={{ initialMount }}>
      <AccordionPrimitive.Root {...props}>{children}</AccordionPrimitive.Root>
    </AccordionContext.Provider>
  );
};

const Accordion = AccordionProvider;

const accordionVariants = cva(
  'typography-ui-17sb flex w-full items-center justify-between transition-all [&[data-state=open]>svg]:rotate-180',
  {
    variants: {
      variant: {
        default: 'px-[12px] ',
        lg: 'px-[24px]',
      },
      size: {
        default: 'h-[56px]',
        sm: 'h-[48px]',
        lg: 'h-[64px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);
const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
    hasBorderBottom?: boolean;
  }
>(({ className, hasBorderBottom = true, ...props }, ref) => {
  const { initialMount } = useAccordionContext();
  return (
    <AccordionPrimitive.Item
      ref={ref}
      className={cn('border-gray-50 border-t-0 border-r-0 border-b-[1px] border-l-0 border-solid', className, {
        'data-[state=closed]:[&>div]:hidden': initialMount !== undefined,
        'border-b-0': !hasBorderBottom,
      })}
      {...props}
    />
  );
});
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & VariantProps<typeof accordionVariants>
>(({ className, children, variant = 'default', size = 'default', ...props }, ref) => (
  <AccordionPrimitive.Header asChild className={'flex items-center justify-between'}>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        accordionVariants({
          variant,
          size,
        }),
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className='h-4 w-4 shrink-0 transition-transform duration-200' />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const { initialMount } = useAccordionContext();

  return (
    <AccordionPrimitive.Content
      ref={ref}
      forceMount={initialMount}
      className='overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'
      {...props}
    >
      <div className={cn('', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
});

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent, useAccordionContext };
